import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { AudioWaveIndicator } from './AudioWaveIndicator';

interface FounderIntroProps {
  founderImageUrl: string;
  introAudioUrl: string;
  title: string;
  onComplete: () => void;
}

export const FounderIntro: React.FC<FounderIntroProps> = ({
  founderImageUrl,
  introAudioUrl,
  title,
  onComplete
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // No auto-play - browsers block it. User must tap to start.

  const handlePlayPause = async () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      await audioRef.current.play();
      setHasStarted(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const percent = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(percent);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(100);
    onComplete();
  };

  return (
    <div className="flex flex-col items-center">
      {/* Founder Avatar with Audio Wave */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative mb-8"
      >
        {/* Glow effect when playing */}
        {isPlaying && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full blur-2xl opacity-30"
            style={{ transform: 'scale(1.2)' }}
          />
        )}

        {/* Avatar Container */}
        <div className="relative w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden shadow-2xl border-4 border-white">
          <img
            src={founderImageUrl}
            alt="Founder"
            className="w-full h-full object-cover"
          />
          
          {/* Play/Pause Overlay - prominent to encourage tap */}
          {!hasStarted && (
            <button
              onClick={handlePlayPause}
              className="absolute inset-0 bg-black/40 flex items-center justify-center group"
            >
              {/* Pulsing ring to draw attention */}
              <motion.div
                className="absolute w-20 h-20 rounded-full border-4 border-[#E5E5E5]0"
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform z-10">
                <Volume2 className="w-8 h-8 text-blue-600" />
              </div>
            </button>
          )}
        </div>

        {/* Audio Wave Indicator */}
        {isPlaying && (
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
            <AudioWaveIndicator isPlaying={isPlaying} />
          </div>
        )}

        {/* Progress Ring */}
        <svg
          className="absolute inset-0 -rotate-90"
          style={{ width: '100%', height: '100%' }}
        >
          <circle
            cx="50%"
            cy="50%"
            r="48%"
            fill="none"
            stroke="rgba(59, 130, 246, 0.2)"
            strokeWidth="4"
          />
          <circle
            cx="50%"
            cy="50%"
            r="48%"
            fill="none"
            stroke="rgb(59, 130, 246)"
            strokeWidth="4"
            strokeDasharray={`${progress * 3} 300`}
            className="transition-all duration-100"
          />
        </svg>
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center"
      >
        {title}
      </motion.h1>

      {/* Status Text */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-gray-500 mb-6"
      >
        {isPlaying 
          ? 'Listening to introduction...' 
          : hasStarted 
            ? 'Introduction complete' 
            : 'Tap to listen to the introduction'}
      </motion.p>

      {/* Replay Button (visible after started) - same size as Start Reading */}
      {hasStarted && (
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={handlePlayPause}
          className="flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-colors min-w-[200px]"
        >
          {isPlaying ? (
            <>
              <Pause className="w-5 h-5 text-gray-700" />
              <span className="font-medium text-gray-700">Pause</span>
            </>
          ) : (
            <>
              <Play className="w-5 h-5 text-gray-700" />
              <span className="font-medium text-gray-700">Replay</span>
            </>
          )}
        </motion.button>
      )}

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={introAudioUrl}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>
  );
};
